import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { filter, fromEvent, map, startWith } from 'rxjs';

import { HelpOpenerService } from '@ideals/partial-views/help';
import { IntercomService } from '@ideals/partial-views/intercom';
import { PageMessagesComponent } from '@ideals/partial-views/page-messages';
import { BrowserService } from '@ideals/services/browser';
import { WINDOW } from '@ideals/types';
import { SpinnerModule } from '@ideals/ui-components/spinner';
import { isInternalUrl, sendMessageToDevice } from '@ideals/utils';

import { DnsPrefetchService } from './core/services/dns-prefetch';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PageMessagesComponent,
    RouterOutlet,
    SpinnerModule,
  ],
  selector: 'auth-root',
  standalone: true,
  templateUrl: './root.component.html',
})
export class RootComponent implements OnInit {
  readonly #browserService = inject(BrowserService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #dnsPrefetchService = inject(DnsPrefetchService);
  readonly #helpOpenerService = inject(HelpOpenerService);
  readonly #intercomService = inject(IntercomService);
  readonly #router = inject(Router);
  readonly #window = inject(WINDOW);

  ngOnInit(): void {
    this.#window.dispatchEvent(new Event('ideals:appInited'));
    this.#intercomService.loadIntercom();
    this.#dnsPrefetchService.createLinks();
    this.#subscribeToHelpQueryParam();

    if (this.#browserService.isStandalone) {
      this.#subscribeToGlobalLinkClick();
    }
  }

  #subscribeToGlobalLinkClick(): void {
    fromEvent(document.body, 'click')
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((event) => {
        const target = event.target as HTMLElement;
        const link: HTMLLinkElement | null = target.tagName === 'A'
          ? target as HTMLLinkElement
          : target.closest<HTMLLinkElement>('a');

        if (link?.href && !isInternalUrl(link.href)) {
          event.preventDefault();
          sendMessageToDevice('openExternalUrl', link.href);
        }
      });
  }

  #subscribeToHelpQueryParam(): void {
    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.#router.routerState.snapshot.root.queryParamMap),
        filter((queryParams) => queryParams.has('help')),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((params) => {
        const section = params.get('help') === 'troubleshooting' ? 'troubleshooting' : 'recommendations';

        this.#helpOpenerService.openHelp(section);

        this.#router.navigate([], {
          queryParams: { help: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      });
  }
}
